/**
 * Mapeo semántico ID de servicio -> slug para Google (SEO Local).
 *
 * El flujo de reserva sigue usando `?service=<id>` intacto: este módulo es solo
 * una tabla de traducción consultiva, sin estado y sin efectos secundarios.
 */

export interface SeoSluggable {
  id?: number;
  name: string;
}

/**
 * Overrides curados para IDs conocidos. Si la BD guarda un nombre distinto o
 * un servicio nuevo aparece, `slugForId` cae al nombre slugificado del catálogo.
 * Ejemplo: id 11 = "Podología General" en producción.
 */
export const SERVICE_SLUGS: Record<number, string> = {
  11: 'podologia-general',
};

/** Normaliza un nombre de servicio a un slug URL seguro y legible. */
export function slugifyServiceName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['’"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Slug semántico para un id conocido (override curado) o derivado del nombre. */
export function slugForId(id: number | undefined, name: string): string {
  if (id && SERVICE_SLUGS[id]) return SERVICE_SLUGS[id];
  return slugifyServiceName(name);
}

/** Slug semántico para un objeto de servicio (la fuente preferida). */
export function slugForService(service: SeoSluggable): string {
  return slugForId(service.id, service.name);
}

/** Busca un servicio en una lista por su slug semántico. */
export function findServiceBySlug<T extends SeoSluggable>(slug: string, services: T[]): T | null {
  const normalized = slug.toLowerCase();
  return services.find((s) => slugForService(s) === normalized) || null;
}
